/**
 * Premium Partner page (templates/page.premium-partners.liquid): metal member cards.
 *
 * <s-pp-tilt>: on fine pointers, tilts its `.ppc-tilt` child (--rx/--ry) and moves the glare (--gx/--gy) on every
 * `.ppc` inside, like the cart and service cards. Touch or reduced motion: static.
 *
 * <s-pp-sim>: the tier simulator. The range input sets data-level (1 Partner, 2 Pro, 3 Elite) from the thresholds in
 * data-thresholds ("5,15"), fills the track (--fill), writes the count + unit and the tier name into the output.
 */
const fine = () => window.matchMedia('(hover: hover) and (pointer: fine)').matches;
const still = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

class SPpTilt extends HTMLElement {
  #ac = null;

  connectedCallback() {
    const target = this.querySelector('.ppc-tilt');
    if (!fine() || still() || !target) return;
    this.#ac = new AbortController();
    const { signal } = this.#ac;
    const max = Number(this.dataset.max || 6);
    let frame = 0;
    this.addEventListener(
      'pointermove',
      (event) => {
        if (frame) return;
        frame = requestAnimationFrame(() => {
          frame = 0;
          const box = this.getBoundingClientRect();
          const x = Math.min(Math.max((event.clientX - box.left) / box.width, 0), 1);
          const y = Math.min(Math.max((event.clientY - box.top) / box.height, 0), 1);
          this.setAttribute('data-tilting', '');
          target.style.setProperty('--ry', `${((x - 0.5) * max).toFixed(2)}deg`);
          target.style.setProperty('--rx', `${((0.5 - y) * max * 0.8).toFixed(2)}deg`);
          target.style.setProperty('--gx', `${(x * 100).toFixed(1)}%`);
          target.style.setProperty('--gy', `${(y * 100).toFixed(1)}%`);
        });
      },
      { signal }
    );
    this.addEventListener(
      'pointerleave',
      () => {
        cancelAnimationFrame(frame);
        frame = 0;
        this.removeAttribute('data-tilting');
        for (const name of ['--rx', '--ry', '--gx', '--gy']) target.style.removeProperty(name);
      },
      { signal }
    );
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#ac = null;
  }
}

class SPpSim extends HTMLElement {
  #ac = null;

  connectedCallback() {
    const input = this.querySelector('input[type="range"]');
    if (!input) return;
    this.#ac = new AbortController();
    const thresholds = (this.dataset.thresholds || '5,15').split(',').map(Number);
    const names = (this.dataset.names || '').split('|');
    const count = this.querySelector('[data-sim-count]');
    const unit = this.querySelector('[data-sim-unit]');
    const tier = this.querySelector('[data-sim-tier]');

    const update = () => {
      const value = Number(input.value);
      const min = Number(input.min || 1);
      const max = Number(input.max || 20);
      let level = 1;
      thresholds.forEach((limit, index) => {
        if (value >= limit) level = index + 2;
      });
      const label = value >= max ? `${value}+` : String(value);
      const unitText = value === 1 ? this.dataset.unitOne : this.dataset.unitOther;
      this.dataset.level = String(level);
      this.style.setProperty('--fill', `${(((value - min) / (max - min)) * 100).toFixed(2)}%`);
      if (count) count.textContent = label;
      if (unit && unitText) unit.textContent = unitText;
      if (tier && names[level - 1]) tier.textContent = names[level - 1];
      input.setAttribute('aria-valuetext', `${label} ${unitText || ''}, ${names[level - 1] || ''}`.trim());
    };

    input.addEventListener('input', update, { signal: this.#ac.signal });
    this.querySelectorAll('[data-sim-jump]').forEach((button) => {
      button.addEventListener(
        'click',
        () => {
          input.value = button.dataset.simJump;
          update();
        },
        { signal: this.#ac.signal }
      );
    });
    update();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#ac = null;
  }
}

if (!customElements.get('s-pp-tilt')) customElements.define('s-pp-tilt', SPpTilt);
if (!customElements.get('s-pp-sim')) customElements.define('s-pp-sim', SPpSim);

/**
 * <s-pp-apply>: sends the application form in the background, so the visitor stays on the page and sees the
 * thank-you state in place (no reload, no detour to /contact). The response is this page rendered with
 * contact_posted=true (the form's return_to), so its #pp-apply-form markup (success or field errors) is swapped in.
 * If Shopify asks for a spam check (challenge page), the form falls back to a normal submit.
 */
class SPpApply extends HTMLElement {
  #ac = null;

  connectedCallback() {
    this.#ac = new AbortController();
    this.addEventListener('submit', (event) => this.#send(event), { signal: this.#ac.signal });
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#ac = null;
  }

  async #send(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || form.dataset.native === 'true') return;
    event.preventDefault();
    const button = form.querySelector('[data-pp-submit]');
    const error = form.querySelector('[data-pp-error]');
    const label = button?.innerHTML;
    if (button) {
      button.disabled = true;
      button.textContent = this.dataset.busy || label;
    }
    if (error) error.hidden = true;

    try {
      const response = await fetch(form.action, { method: 'POST', body: new FormData(form), headers: { Accept: 'text/html' } });
      if (/\/challenge/.test(response.url)) {
        form.dataset.native = 'true';
        form.submit();
        return;
      }
      const html = await response.text();
      const fresh = new DOMParser().parseFromString(html, 'text/html').getElementById(form.id);
      if (fresh) {
        form.innerHTML = fresh.innerHTML;
      } else if (response.ok || /contact_posted=true/.test(response.url)) {
        const tpl = this.querySelector('[data-pp-success-tpl]');
        if (tpl) form.replaceChildren(tpl.content.cloneNode(true));
      } else {
        throw new Error(String(response.status));
      }
      const focusTarget = form.querySelector('[data-pp-success], [role="alert"]');
      focusTarget?.focus({ preventScroll: true });
      this.scrollIntoView({ behavior: still() ? 'auto' : 'smooth', block: 'center' });
    } catch {
      if (button) {
        button.disabled = false;
        button.innerHTML = label;
      }
      if (error) {
        error.textContent = this.dataset.error || '';
        error.hidden = false;
      }
    }
  }
}

if (!customElements.get('s-pp-apply')) customElements.define('s-pp-apply', SPpApply);

/** <s-pp-founders>: click / Enter on a founding card flips it (aria-pressed); hover flips on fine pointers in CSS. */
class SPpFounders extends HTMLElement {
  #ac = null;

  connectedCallback() {
    this.#ac = new AbortController();
    this.addEventListener(
      'click',
      (event) => {
        const button = event.target instanceof Element ? event.target.closest('[data-fc-toggle]') : null;
        if (!button) return;
        button.setAttribute('aria-pressed', button.getAttribute('aria-pressed') === 'true' ? 'false' : 'true');
      },
      { signal: this.#ac.signal }
    );
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#ac = null;
  }
}

if (!customElements.get('s-pp-founders')) customElements.define('s-pp-founders', SPpFounders);
